import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import useSiteSearch from "../hooks/useSiteSearch";
import SearchDropdownResults from "./SearchDropdownResults";

// Mobile / tablet search field + live dropdown (top-bar row + drawer). Shares the
// grouped, icon-led result rows with the desktop panel via SearchDropdownResults,
// so the whole search experience looks consistent across breakpoints.
//
// Props:
//   autoFocus   — focus the input on mount (top-bar row: true, drawer: false)
//   onClose     — called after navigating (header closes its panel/menu)
//   placeholder — input placeholder text
export default function SearchBox({
  autoFocus = true,
  onClose,
  placeholder = "Search anything…",
}) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { query, setQuery, results, total, suggestion, loading, error } = useSiteSearch({
    limit: 6,
  });

  useEffect(() => {
    if (!autoFocus) return;
    const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
    return () => clearTimeout(id);
  }, [autoFocus]);

  const go = (url) => {
    setQuery("");
    if (onClose) onClose();
    navigate(url);
  };

  const submit = (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (q.length < 2) return;
    go(`/search?q=${encodeURIComponent(q)}`);
  };

  const trimmed = query.trim();
  const showPanel = trimmed.length >= 2;
  const items = results.slice(0, 6);

  return (
    <div className="relative w-full">
      <form
        onSubmit={submit}
        className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-full px-4 py-3 shadow-sm focus-within:border-[#F26322] focus-within:ring-2 focus-within:ring-[#F26322]/15 transition-all"
      >
        <Search size={20} className="text-[#F26322] shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label="Search the site"
          className="flex-1 min-w-0 bg-transparent outline-none text-[15.5px] text-gray-800 placeholder:text-gray-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={16} />
          </button>
        )}
      </form>

      {showPanel && (
        <div
          className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-100 rounded-2xl z-[70] max-h-[70vh] overflow-y-auto"
          style={{ boxShadow: "0px 18px 50px -14px rgba(16,24,40,0.28)" }}
        >
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 rounded-full border-2 border-gray-200 border-t-[#F26322] animate-spin" />
            </div>
          )}

          {/* A failed request isn't "no matches" — say so instead. */}
          {!loading && error && (
            <div className="px-4 py-5 text-[13px] text-gray-500">
              Search is unavailable right now. Please try again.
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="px-4 py-5 text-[13px] text-gray-500">
              No matches for “{trimmed}”.
              {suggestion && (
                <>
                  {" "}
                  Did you mean{" "}
                  <button
                    onClick={() => setQuery(suggestion)}
                    className="text-[#F26322] font-[600] hover:underline"
                  >
                    {suggestion}
                  </button>
                  ?
                </>
              )}
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              {suggestion && (
                <div className="px-4 py-2 text-[12px] text-gray-500 bg-gray-50/70 border-b border-gray-50">
                  Did you mean{" "}
                  <button
                    onClick={() => setQuery(suggestion)}
                    className="text-[#F26322] font-[600] hover:underline"
                  >
                    {suggestion}
                  </button>
                  ?
                </div>
              )}
              <SearchDropdownResults results={items} query={trimmed} onSelect={go} />
              <button
                onClick={submit}
                className="w-full text-left px-4 py-3.5 border-t border-gray-100 bg-gray-50/70 text-[14px] font-[600] text-[#F26322] hover:bg-[#F26322]/5 transition-colors"
              >
                View all {total} result{total === 1 ? "" : "s"} →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
